import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star, CheckCircle2, Lock, ArrowRight, Activity, Brain, Shield, ChevronRight, Baby, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { AuthGate } from './AuthGate';
import { calculateFertilityScore } from '../lib/fertilityScore';
import { trackEvent } from '../lib/posthog';
import { openPaddleCheckout, previewPrices } from '../lib/paddle';
import { persistOnboarding } from '../lib/persistOnboarding';
import { useTranslation } from 'react-i18next';

type StepType = 'landing' | 'single' | 'multiple' | 'edu_social' | 'edu_didyouknow' | 'edu_science' | 'pre_analysis' | 'loading' | 'wow' | 'paywall';

interface Step {
  id: string;
  type: StepType;
  titleKey?: string;
  subtitleKey?: string;
  /** Each option is a semantic key; displayed via t(`onboarding.questions.${step.id}.${optionKey}`) */
  options?: string[];
}

const commonSteps: Step[] = [
  { id: 'landing', type: 'landing' },
  { id: 'intent', type: 'multiple', titleKey: 'onboarding.questions.intent.title', options: ['child_now', 'difficulties', 'optimize_future', 'reduce_risks', 'know_potential', 'maximize'] },
  { id: 'gender', type: 'single', titleKey: 'onboarding.questions.gender.title', options: ['female', 'male'] },
  { id: 'age', type: 'single', titleKey: 'onboarding.questions.age.title', options: ['18_24', '25_29', '30_34', '35_39', '40_plus'] },
  { id: 'bmi', type: 'single', titleKey: 'onboarding.questions.bmi.title', options: ['underweight', 'normal', 'overweight', 'obese', 'unknown'] },
  { id: 'tobacco', type: 'single', titleKey: 'onboarding.questions.tobacco.title', options: ['yes_regularly', 'occasionally', 'never'] },
  { id: 'alcohol', type: 'single', titleKey: 'onboarding.questions.alcohol.title', options: ['regularly', 'occasionally', 'never'] },
  { id: 'edu1', type: 'edu_social' },
  { id: 'drugs', type: 'single', titleKey: 'onboarding.questions.drugs.title', options: ['yes', 'no'] },
  { id: 'diet', type: 'single', titleKey: 'onboarding.questions.diet.title', options: ['balanced', 'vegetarian', 'fast_food', 'unknown'] },
  { id: 'toxins', type: 'single', titleKey: 'onboarding.questions.toxins.title', options: ['professional_exposure', 'strong_household', 'no', 'unknown'] },
  { id: 'sleep', type: 'single', titleKey: 'onboarding.questions.sleep.title', options: ['less_than_6', '6_to_8', 'more_than_8'] },
  { id: 'edu2', type: 'edu_didyouknow' },
  { id: 'clothes', type: 'single', titleKey: 'onboarding.questions.clothes.title', options: ['often', 'sometimes', 'rarely'] },
];

const femaleSteps: Step[] = [
  { id: 'f_cycles', type: 'single', titleKey: 'onboarding.questions.f_cycles.title', options: ['yes', 'no', 'unknown'] },
  { id: 'f_pain', type: 'single', titleKey: 'onboarding.questions.f_pain.title', options: ['yes', 'no', 'unknown'] },
  { id: 'f_preg', type: 'single', titleKey: 'onboarding.questions.f_preg.title', options: ['yes', 'no'] },
  { id: 'edu3', type: 'edu_science' },
  { id: 'f_surg', type: 'single', titleKey: 'onboarding.questions.f_surg.title', options: ['yes', 'no'] },
  { id: 'f_exe', type: 'single', titleKey: 'onboarding.questions.f_exe.title', options: ['never', '1_to_2_per_week', '3_to_5_per_week', 'daily'] },
  { id: 'f_hist', type: 'multiple', titleKey: 'onboarding.questions.f_hist.title', options: ['pcos', 'thyroid', 'none', 'other'] },
  { id: 'f_stress', type: 'single', titleKey: 'onboarding.questions.f_stress.title', options: ['low', 'moderate', 'high'] },
  { id: 'doctor_ref', type: 'single', titleKey: 'onboarding.questions.doctor_ref.title', options: ['yes', 'no'] },
  { id: 'f_freq', type: 'single', titleKey: 'onboarding.questions.f_freq.title', options: ['less_than_once_week', '1_to_2_per_week', '3_plus_per_week'] },
];

const maleSteps: Step[] = [
  { id: 'm_sex', type: 'single', titleKey: 'onboarding.questions.m_sex.title', subtitleKey: 'onboarding.questions.m_sex.subtitle', options: ['yes', 'no'] },
  { id: 'm_surg', type: 'single', titleKey: 'onboarding.questions.m_surg.title', options: ['yes', 'no'] },
  { id: 'm_ster', type: 'single', titleKey: 'onboarding.questions.m_ster.title', options: ['yes_currently', 'yes_past', 'never'] },
  { id: 'edu3', type: 'edu_science' },
  { id: 'm_sit', type: 'single', titleKey: 'onboarding.questions.m_sit.title', options: ['less_than_4', '4_to_8', 'more_than_8'] },
  { id: 'm_exe', type: 'single', titleKey: 'onboarding.questions.m_exe.title', options: ['never', '1_to_2_per_week', '3_to_5_per_week', 'daily'] },
  { id: 'm_hist', type: 'multiple', titleKey: 'onboarding.questions.m_hist.title', options: ['diabetes', 'hypertension', 'none', 'other'] },
  { id: 'm_freq', type: 'single', titleKey: 'onboarding.questions.m_freq.title', options: ['less_than_once_week', '1_to_2_per_week', '3_plus_per_week'] },
  { id: 'doctor_ref', type: 'single', titleKey: 'onboarding.questions.doctor_ref.title', options: ['yes', 'no'] },
  { id: 'm_under', type: 'single', titleKey: 'onboarding.questions.m_under.title', options: ['yes_often', 'no_loose'] },
  { id: 'm_stress', type: 'single', titleKey: 'onboarding.questions.m_stress.title', options: ['low', 'moderate', 'high'] },
];

const finalSteps: Step[] = [
  { id: 'pre_analysis', type: 'pre_analysis' },
  { id: 'loading', type: 'loading' },
  { id: 'wow', type: 'wow' },
  { id: 'paywall', type: 'paywall' },
];

export function OnboardingFlow() {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [gender, setGender] = useState<'female' | 'male' | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [dynamicScore, setDynamicScore] = useState(85);
  const [showSignIn, setShowSignIn] = useState(false);

  const currentFlow = [
    ...commonSteps,
    ...(gender === 'female' ? femaleSteps : gender === 'male' ? maleSteps : []),
    ...finalSteps
  ];

  const currentStep = currentFlow[stepIndex];
  const progress = (stepIndex / (currentFlow.length - 1)) * 100;

  // Recalculate deterministic score when answers change
  useEffect(() => {
    const score = calculateFertilityScore({ gender, answers });
    setDynamicScore(score);
    trackEvent('onboarding_score_calculated', { score, gender });
    sessionStorage.setItem('atilum_onboarding', JSON.stringify({ score, answers, gender }));
  }, [answers, gender]);

  const handleNext = () => {
    if (stepIndex < currentFlow.length - 1) {
      setStepIndex(prev => prev + 1);
    }
  };

  const handleAnswer = (value: string | string[]) => {
    // Values are already semantic keys — store directly
    trackEvent('onboarding_question_answered', { question_id: currentStep.id, answer: value });
    setAnswers(prev => ({ ...prev, [currentStep.id]: value }));
    if (currentStep.id === 'gender') {
      setGender(value as 'female' | 'male');
    }
    if (currentStep.type === 'single') {
      setTimeout(handleNext, 300);
    }
  };

  const isQuestion = currentStep?.type === 'single' || currentStep?.type === 'multiple';
  const showHeader = currentStep?.type !== 'landing' && currentStep?.type !== 'loading' && currentStep?.type !== 'wow' && currentStep?.type !== 'paywall';

  /** Translate an option key for the current step */
  const tOpt = (stepId: string, optKey: string) => t(`onboarding.questions.${stepId}.${optKey}`);

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900 flex flex-col relative overflow-hidden selection:bg-emerald-200 selection:text-emerald-900">
      {/* Noise Overlay */}
      <svg className="pointer-events-none fixed inset-0 z-50 h-full w-full opacity-[0.03] mix-blend-overlay">
        <filter id="noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noise)" />
      </svg>

      {/* Header & Progress */}
      <AnimatePresence>
        {showHeader && (
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="pt-12 px-6 pb-4 flex flex-col gap-4 relative z-20"
          >
            <div className="flex justify-between items-center">
              <button
                onClick={() => setStepIndex(prev => Math.max(0, prev - 1))}
                className="text-stone-400 font-bold text-sm uppercase tracking-widest hover:text-stone-600 transition-colors"
              >
                {t('common.back')}
              </button>
            </div>
            <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-emerald-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              />
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      <main className="flex-1 flex flex-col max-w-md w-full mx-auto px-6 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep?.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            className="flex-1 flex flex-col justify-center py-8"
          >
            {currentStep?.type === 'landing' && <LandingScreen onNext={handleNext} onLogin={() => setShowSignIn(true)} />}
            {currentStep?.type === 'single' && <SingleChoiceScreen step={currentStep} onAnswer={handleAnswer} selected={answers[currentStep.id] as string} tOpt={tOpt} />}
            {currentStep?.type === 'multiple' && <MultipleChoiceScreen step={currentStep} onAnswer={handleAnswer} selected={(answers[currentStep.id] as string[]) || []} onNext={handleNext} tOpt={tOpt} />}
            {currentStep?.type === 'edu_social' && <EduSocialScreen onNext={handleNext} />}
            {currentStep?.type === 'edu_didyouknow' && <EduDidYouKnowScreen onNext={handleNext} />}
            {currentStep?.type === 'edu_science' && <EduScienceScreen onNext={handleNext} />}
            {currentStep?.type === 'pre_analysis' && <PreAnalysisScreen onNext={handleNext} />}
            {currentStep?.type === 'loading' && <LoadingScreen onNext={handleNext} />}
            {currentStep?.type === 'wow' && <WowScreen onNext={handleNext} score={dynamicScore} />}
            {currentStep?.type === 'paywall' && <PaywallScreen />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Auth Gate overlay (sign-in from landing) */}
      {showSignIn && (
        <AuthGate
          onClose={() => setShowSignIn(false)}
          initialMode="signin"
          onSignUpSuccess={persistOnboarding}
        />
      )}
    </div>
  );
}

// --- Screen Components ---

function LandingScreen({ onNext, onLogin }: { onNext: () => void, onLogin: () => void }) {
  const { t } = useTranslation();

  const handleStart = () => {
    trackEvent('onboarding_started');
    onNext();
  };

  return (
    <div className="flex flex-col pb-8 gap-12">
      <div className="flex justify-end pt-4">
        <button onClick={onLogin} className="text-sm font-bold text-stone-500 hover:text-stone-900 transition-colors">
          {t('onboarding.signIn')}
        </button>
      </div>

      {/* Hero */}
      <div className="flex flex-col items-center text-center gap-6">
        <div className="w-20 h-20 bg-emerald-100 rounded-[2rem] flex items-center justify-center mb-4 rotate-3">
          <Activity className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="text-5xl font-extrabold tracking-tighter text-stone-900 leading-[1.1]">
          {t('onboarding.headline')}
        </h1>
        <p className="text-lg font-medium text-stone-500 tracking-tight px-4">
          {t('onboarding.subheadline')}
        </p>
      </div>

      {/* CTA */}
      <button
        onClick={handleStart}
        className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-stone-800 hover:scale-[1.02] transition-all active:scale-95"
      >
        {t('onboarding.start')}
      </button>

      {/* How it works */}
      <div className="flex flex-col gap-5">
        <h2 className="text-2xl font-extrabold tracking-tight text-stone-900 text-center">{t('landing.howItWorks')}</h2>
        <div className="flex flex-col gap-3">
          <div className="bg-white rounded-2xl p-5 border border-stone-100 flex items-start gap-4">
            <div className="bg-emerald-50 rounded-xl p-2.5 shrink-0"><span className="text-lg font-black text-emerald-600">1</span></div>
            <div><h3 className="font-bold text-stone-900 text-sm">{t('landing.step1Title')}</h3><p className="text-xs text-stone-500 mt-1">{t('landing.step1Desc')}</p></div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-stone-100 flex items-start gap-4">
            <div className="bg-emerald-50 rounded-xl p-2.5 shrink-0"><span className="text-lg font-black text-emerald-600">2</span></div>
            <div><h3 className="font-bold text-stone-900 text-sm">{t('landing.step2Title')}</h3><p className="text-xs text-stone-500 mt-1">{t('landing.step2Desc')}</p></div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-stone-100 flex items-start gap-4">
            <div className="bg-emerald-50 rounded-xl p-2.5 shrink-0"><span className="text-lg font-black text-emerald-600">3</span></div>
            <div><h3 className="font-bold text-stone-900 text-sm">{t('landing.step3Title')}</h3><p className="text-xs text-stone-500 mt-1">{t('landing.step3Desc')}</p></div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="flex flex-col gap-5">
        <h2 className="text-2xl font-extrabold tracking-tight text-stone-900 text-center">{t('landing.featuresTitle')}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center">
            <Activity className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <span className="text-xs font-bold text-stone-700">{t('landing.feature1')}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center">
            <Brain className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <span className="text-xs font-bold text-stone-700">{t('landing.feature2')}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center">
            <Shield className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <span className="text-xs font-bold text-stone-700">{t('landing.feature3')}</span>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <span className="text-xs font-bold text-stone-700">{t('landing.feature4')}</span>
          </div>
        </div>
      </div>

      {/* Trust / Science */}
      <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100 text-center">
        <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
        <h3 className="font-bold text-emerald-900 mb-2">{t('landing.trustTitle')}</h3>
        <p className="text-sm text-emerald-700/80">{t('landing.trustDesc')}</p>
      </div>

      {/* Second CTA */}
      <button
        onClick={handleStart}
        className="w-full bg-emerald-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-emerald-600 hover:scale-[1.02] transition-all active:scale-95"
      >
        {t('onboarding.start')}
      </button>

      {/* Medical disclaimer before footer */}
      <div className="bg-stone-100 rounded-2xl p-4 border border-stone-200">
        <p className="text-xs font-medium text-stone-500 text-center leading-relaxed">
          {t('legal.medicalDisclaimer')}
        </p>
      </div>

      {/* Footer links */}
      <div className="flex flex-col items-center gap-3 text-xs text-stone-400 font-semibold pt-4">
        <div className="flex gap-4">
          <a href="/pricing.html" className="hover:text-stone-600 transition-colors">{t('landing.pricing')}</a>
          <a href="/terms.html" className="hover:text-stone-600 transition-colors">{t('landing.terms')}</a>
          <a href="/privacy.html" className="hover:text-stone-600 transition-colors">{t('landing.privacy')}</a>
          <a href="/refund.html" className="hover:text-stone-600 transition-colors">{t('landing.refund')}</a>
          <a href="/about.html" className="hover:text-stone-600 transition-colors">About</a>
          <a href="mailto:contact@atilum.com" className="hover:text-stone-600 transition-colors">{t('landing.contact')}</a>
        </div>
        <p>© 2026 Atilum. {t('landing.allRights')}</p>
      </div>
    </div>
  );
}

function SingleChoiceScreen({ step, onAnswer, selected, tOpt }: { step: Step, onAnswer: (v: string) => void, selected?: string, tOpt: (stepId: string, optKey: string) => string }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full">
      <div className="mb-10">
        <h2 className="text-3xl font-extrabold tracking-tighter text-stone-900 leading-tight">{step.titleKey ? t(step.titleKey) : ''}</h2>
        {step.subtitleKey && <p className="text-stone-500 font-medium mt-2">{t(step.subtitleKey)}</p>}
      </div>
      <div className="flex flex-col gap-3">
        {step.options?.map(opt => (
          <button
            key={opt}
            onClick={() => onAnswer(opt)}
            className={`
              w-full p-5 rounded-2xl text-left font-bold transition-all duration-300 border-2
              ${selected === opt
                ? 'bg-stone-900 text-white border-stone-900 scale-[1.02]'
                : 'bg-white text-stone-600 border-stone-100 hover:border-stone-300 hover:bg-stone-50'}
            `}
          >
            {tOpt(step.id, opt)}
          </button>
        ))}
      </div>
    </div>
  );
}

function MultipleChoiceScreen({ step, onAnswer, selected, onNext, tOpt }: { step: Step, onAnswer: (v: string[]) => void, selected: string[], onNext: () => void, tOpt: (stepId: string, optKey: string) => string }) {
  const { t } = useTranslation();

  const toggle = (opt: string) => {
    if (selected.includes(opt)) {
      onAnswer(selected.filter(o => o !== opt));
    } else {
      onAnswer([...selected, opt]);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-10">
        <h2 className="text-3xl font-extrabold tracking-tighter text-stone-900 leading-tight">{step.titleKey ? t(step.titleKey) : ''}</h2>
        <p className="text-stone-400 font-bold text-xs uppercase tracking-widest mt-3">{t('onboarding.multipleChoice')}</p>
      </div>
      <div className="flex flex-col gap-3 flex-1 overflow-y-auto pb-4">
        {step.options?.map(opt => {
          const isSelected = selected.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className={`
                w-full p-5 rounded-2xl text-left font-bold transition-all duration-300 border-2 flex justify-between items-center
                ${isSelected
                  ? 'bg-stone-900 text-white border-stone-900'
                  : 'bg-white text-stone-600 border-stone-100 hover:border-stone-300 hover:bg-stone-50'}
              `}
            >
              <span>{tOpt(step.id, opt)}</span>
              {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            </button>
          );
        })}
      </div>
      <div className="pt-4 mt-auto">
        <button
          onClick={onNext}
          disabled={selected.length === 0}
          className="w-full bg-emerald-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
        >
          {t('common.continue')}
        </button>
      </div>
    </div>
  );
}

function EduSocialScreen({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full justify-center text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="flex justify-center gap-1 mb-6"
      >
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-8 h-8 fill-amber-400 text-amber-400" />
        ))}
      </motion.div>
      <h2 className="text-4xl font-extrabold tracking-tighter text-stone-900 mb-4">{t('onboarding.social.title')}</h2>
      <p className="text-lg font-medium text-stone-600 mb-12">
        {t('onboarding.social.satisfaction')}
      </p>

      <div className="flex flex-col gap-4 text-left mb-12">
        <ReviewCard name={t('onboarding.social.review1Name')} text={t('onboarding.social.review1Text')} />
        <ReviewCard name={t('onboarding.social.review2Name')} text={t('onboarding.social.review2Text')} />
        <ReviewCard name={t('onboarding.social.review3Name')} text={t('onboarding.social.review3Text')} />
      </div>

      <button onClick={onNext} className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-stone-800 transition-all active:scale-95 mt-auto">
        {t('common.continue')}
      </button>
    </div>
  );
}

function ReviewCard({ name, text }: { name: string, text: string }) {
  return (
    <div className="bg-white p-4 rounded-2xl shadow-sm border border-stone-100">
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold text-stone-900">{name}</span>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
        </div>
      </div>
      <p className="text-sm font-medium text-stone-500">{text}</p>
    </div>
  );
}

function EduDidYouKnowScreen({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();
  const [answered, setAnswered] = useState(false);

  return (
    <div className="flex flex-col h-full justify-center">
      <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-8">
        <Brain className="w-8 h-8 text-emerald-600" />
      </div>
      <h2 className="text-3xl font-extrabold tracking-tighter text-stone-900 mb-8 leading-tight">
        {t('onboarding.didYouKnow.question')}
      </h2>

      {!answered ? (
        <div className="flex gap-4">
          <button onClick={() => setAnswered(true)} className="flex-1 bg-white border-2 border-stone-200 text-stone-700 py-4 rounded-2xl font-bold hover:border-stone-900 transition-colors">
            {t('common.yes')}
          </button>
          <button onClick={() => setAnswered(true)} className="flex-1 bg-white border-2 border-stone-200 text-stone-700 py-4 rounded-2xl font-bold hover:border-stone-900 transition-colors">
            {t('common.no')}
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-8">
          <p className="text-lg font-medium text-emerald-700 bg-emerald-50 p-6 rounded-2xl border border-emerald-100">
            {t('onboarding.didYouKnow.explanation')}
          </p>
          <button onClick={onNext} className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-stone-800 transition-all active:scale-95">
            {t('common.continue')}
          </button>
        </motion.div>
      )}
    </div>
  );
}

function EduScienceScreen({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full justify-center">
      <div className="bg-stone-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-8">
        <Shield className="w-8 h-8 text-stone-700" />
      </div>
      <h2 className="text-4xl font-extrabold tracking-tighter text-stone-900 mb-6">{t('onboarding.science.title')}</h2>
      <p className="text-xl font-medium text-stone-600 mb-12 leading-relaxed">
        {t('onboarding.science.text')}
      </p>
      <button onClick={onNext} className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-stone-800 transition-all active:scale-95 mt-auto">
        {t('common.continue')}
      </button>
    </div>
  );
}

function PreAnalysisScreen({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full justify-center text-center">
      <div className="flex justify-center gap-1 mb-8">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-10 h-10 fill-amber-400 text-amber-400" />
        ))}
      </div>
      <h2 className="text-4xl font-extrabold tracking-tighter text-stone-900 mb-6">{t('onboarding.preAnalysis.title')}</h2>
      <p className="text-lg font-medium text-stone-600 mb-12">
        {t('onboarding.preAnalysis.subtitle')}
      </p>
      <button onClick={onNext} className="w-full bg-emerald-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-emerald-600 transition-all active:scale-95 mt-auto">
        {t('onboarding.preAnalysis.cta')}
      </button>
    </div>
  );
}

function LoadingScreen({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const duration = 7000;
    const interval = 50;
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setProgress((currentStep / steps) * 100);
      if (currentStep >= steps) {
        clearInterval(timer);
        onNext();
      }
    }, interval);

    return () => clearInterval(timer);
  }, [onNext]);

  return (
    <div className="flex flex-col h-full justify-center items-center text-center">
      <div className="relative w-32 h-32 mb-12">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#f5f5f4" strokeWidth="8" />
          <motion.circle
            cx="50" cy="50" r="45" fill="none" stroke="#10b981" strokeWidth="8"
            strokeDasharray="283"
            strokeDashoffset={283 - (283 * progress) / 100}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-stone-900">{Math.round(progress)}%</span>
        </div>
      </div>
      <h2 className="text-3xl font-extrabold tracking-tighter text-stone-900 mb-4">{t('onboarding.loading.title')}</h2>
      <p className="text-lg font-medium text-stone-500">{t('onboarding.loading.subtitle')}</p>
    </div>
  );
}

function WowScreen({ onNext, score }: { onNext: () => void, score: number }) {
  const { t } = useTranslation();

  const wowChartData = [
    { name: t('onboarding.wow.today'), score: 40 },
    { name: "", score: 55 },
    { name: "", score: 70 },
    { name: "", score: 82 },
    { name: t('onboarding.wow.afterAtilum'), score: 88 },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="text-center mb-8">
        <h2 className="text-xl font-bold text-stone-500 mb-2">{t('onboarding.wow.scoreTitle')}</h2>
        <div className="text-7xl font-black tracking-tighter text-stone-900">
          {score}<span className="text-3xl text-stone-400">/100</span>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-stone-100 mb-6">
        <div className="flex justify-between items-center mb-4">
          <span className="font-bold text-stone-700">{t('onboarding.wow.improvement')}</span>
          <span className="font-black text-emerald-500">{t('onboarding.wow.possible')}</span>
        </div>
        <div className="h-40 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={wowChartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="colorScore" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#d6d3d1" />
                  <stop offset="100%" stopColor="#10b981" />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#a8a29e', fontSize: 10, fontWeight: 700 }} dy={10} />
              <YAxis hide domain={[30, 100]} />
              <Line type="monotone" dataKey="score" stroke="url(#colorScore)" strokeWidth={4} dot={{ r: 0 }} activeDot={{ r: 6, fill: '#10b981', strokeWidth: 0 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-stone-100 mb-8 relative overflow-hidden">
        <h3 className="font-bold text-stone-900 mb-4">{t('onboarding.wow.program')}</h3>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <span className="font-bold text-stone-700">{t('onboarding.wow.sleepImprovement')}</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <span className="font-bold text-stone-700">{t('onboarding.wow.toxinReduction')}</span>
          </div>
          <div className="flex items-center gap-3 blur-[4px] opacity-60">
            <Lock className="w-5 h-5 text-stone-400" />
            <span className="font-bold text-stone-700">{t('onboarding.wow.hormonalPlan')}</span>
          </div>
          <div className="flex items-center gap-3 blur-[4px] opacity-60">
            <Lock className="w-5 h-5 text-stone-400" />
            <span className="font-bold text-stone-700">{t('onboarding.wow.nutritionPlan')}</span>
          </div>
        </div>
        {/* Gradient overlay to enhance blur effect */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </div>

      <button onClick={onNext} className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-stone-800 transition-all active:scale-95 mt-auto">
        {t('common.continue')}
      </button>
    </div>
  );
}

const monthlyPriceId = import.meta.env.VITE_PADDLE_PRICE_ID_MONTHLY as string;
const annualPriceId = import.meta.env.VITE_PADDLE_PRICE_ID_ANNUAL as string | undefined;

function PaywallScreen() {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState<Record<string, string>>({});

  useEffect(() => { trackEvent('paywall_viewed'); }, []);

  useEffect(() => {
    const ids = [monthlyPriceId, annualPriceId].filter(Boolean) as string[];
    if (ids.length === 0) return;
    previewPrices(ids).then(items => {
      const map: Record<string, string> = {};
      items.forEach(i => { map[i.priceId] = i.formatted; });
      setPrices(map);
    }).catch(console.error);
  }, []);

  const selectedPriceId = plan === 'annual' && annualPriceId ? annualPriceId : monthlyPriceId;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      await openPaddleCheckout({
        priceId: selectedPriceId,
      });
    } finally {
      setLoading(false);
    }
  };

  const monthlyLabel = prices[monthlyPriceId] ?? '…';
  const annualLabel = annualPriceId ? (prices[annualPriceId] ?? '…') : null;

  return (
    <div className="flex flex-col h-full justify-center">
      <div className="bg-emerald-100 w-20 h-20 rounded-[2rem] flex items-center justify-center mb-8 mx-auto">
        <Lock className="w-10 h-10 text-emerald-600" />
      </div>
      <h2 className="text-4xl font-extrabold tracking-tighter text-stone-900 mb-6 text-center leading-tight">
        {t('onboarding.paywall.title')}
      </h2>
      <p className="text-lg font-medium text-stone-600 mb-8 text-center px-4">
        {t('onboarding.paywall.subtitle')}
      </p>

      <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-stone-100 mb-6">
        <h3 className="font-bold text-stone-900 mb-4 text-center">{t('onboarding.paywall.riskTitle')}</h3>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 flex flex-col items-center p-4 bg-stone-50 rounded-2xl border border-stone-100 grayscale opacity-70">
            <Baby className="w-10 h-10 text-stone-500 mb-2" />
            <span className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">{t('onboarding.paywall.before')}</span>
            <span className="text-[10px] text-center text-stone-400 leading-tight">{t('onboarding.paywall.beforeDesc')}</span>
          </div>
          <ArrowRight className="w-6 h-6 text-stone-300 shrink-0" />
          <div className="flex-1 flex flex-col items-center p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
            <Baby className="w-10 h-10 text-emerald-500 mb-2" />
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-1">{t('onboarding.paywall.after')}</span>
            <span className="text-[10px] text-center text-emerald-700/80 leading-tight">{t('onboarding.paywall.afterDesc')}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-stone-100 mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2 rounded-full"><CheckCircle2 className="w-5 h-5 text-emerald-500" /></div>
            <span className="font-bold text-stone-700">{t('onboarding.paywall.dailyTracking')}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2 rounded-full"><CheckCircle2 className="w-5 h-5 text-emerald-500" /></div>
            <span className="font-bold text-stone-700">{t('onboarding.paywall.nutrition')}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2 rounded-full"><CheckCircle2 className="w-5 h-5 text-emerald-500" /></div>
            <span className="font-bold text-stone-700">{t('onboarding.paywall.analyses')}</span>
          </div>
        </div>
      </div>

      {/* Plan selector */}
      {annualPriceId && (
        <div className="flex gap-3 mb-6">
          <PlanToggle active={plan === 'monthly'} onClick={() => { setPlan('monthly'); trackEvent('plan_selected', { plan: 'monthly' }); }} label={t('paywallGate.monthly')} price={monthlyLabel} suffix={t('paywallGate.perMonth')} />
          <PlanToggle active={plan === 'annual'} onClick={() => { setPlan('annual'); trackEvent('plan_selected', { plan: 'annual' }); }} label={t('paywallGate.annual')} price={annualLabel ?? '…'} suffix={t('paywallGate.perYear')} badge="-40%" />
        </div>
      )}

      <button onClick={handleCheckout} disabled={loading} className="w-full bg-emerald-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-emerald-600 transition-all active:scale-95 mt-auto flex items-center justify-center gap-2 disabled:opacity-60">
        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
        {t('onboarding.paywall.cta')} <ChevronRight className="w-5 h-5" />
      </button>

      <p className="text-xs font-medium text-stone-400 text-center mt-4 leading-relaxed">
        {t('legal.medicalDisclaimer')}
      </p>
    </div>
  );
}

function PlanToggle({ active, onClick, label, price, suffix, badge }: {
  active: boolean; onClick: () => void; label: string; price: string; suffix: string; badge?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 p-4 rounded-2xl border-2 transition-all text-left relative ${
        active ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 bg-white'
      }`}
    >
      {badge && (
        <span className="absolute -top-2.5 right-3 bg-emerald-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase">{badge}</span>
      )}
      <span className="text-xs font-bold text-stone-500 uppercase tracking-widest">{label}</span>
      <div className="mt-1">
        <span className="text-xl font-black text-stone-900">{price}</span>
        <span className="text-xs text-stone-400 font-semibold">{suffix}</span>
      </div>
    </button>
  );
}
